import { useState, useEffect } from "react";
import Table from "../../../components/table";
import Button from "../../../components/button";
import { FaRegEdit } from "react-icons/fa";
import { AiOutlineDelete } from "react-icons/ai";
import { BsInfoCircle } from "react-icons/bs";
import AddCourtModal from "./addCourtModal";
import axios from "axios";
import { courtFetch } from "../../../api/endpoint";
import EditCourtModal from "./editCourtModal";
import RemoveCourtModal from "./removeCourtModal";
import { useDispatch, useSelector } from "react-redux";
import { saveCourtsData } from "../../../store/slice/courtsSlice";
import Loader from "../../../components/loader";
import AddMultipleCourtsModal from "./addMultipleCourtsModal";
import Input from "../../../components/input";
import CourtDetailsModal from "./courtDetailsModal";

const CourtsList = () => {
  const dispatch = useDispatch();
  const [filteredCourts, setFilteredCourts] = useState([]);
  const [sportsList, setSportsList] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isAddBulkOpen, setIsAddBulkOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: "",
    sport: "",
    access: "",
    city: "",
    longitude: "",
    latitude: "",
    distance: "",
    address: "",
  });
  const [isDropdownFilterOpen, setIsDropdownFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState();
  const [totalPages, setTotalPages] = useState();

  const { auth } = useSelector((state) => state.login);
  const { sports } = useSelector((state) => state.sports);

  useEffect(() => {
    fetchCourts(filters);
  }, [currentPage]);

  useEffect(() => {
    if (sports) {
      const sportsData = sports?.map((sport) => ({
        value: sport?.sportName,
        label: sport?.sportName,
      }));
      setSportsList([{ value: "", label: "Select a sport" }, ...sportsData]);
    }
  }, [sports]);

  const fetchCourts = async (filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          if (key === "distance") {
            const distanceInMeters = parseFloat(value) * 1609.34;
            params.append(key, distanceInMeters);
          } else {
            params.append(key, value);
          }
        }
      });

      params.append("page", currentPage);
      params.append("size", pageSize);

      const response = await axios.get(`${courtFetch}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${auth}` },
      });

      const { content, totalElements, totalPages } = response?.data;

      const courtsData = content?.map((court) => ({
        id: court?.id,
        name: court?.courtName,
        address: court?.address,
        amenities: court?.amenities ? court?.amenities?.join(", ") : "N/A",
        actions: () => getActionButtons(court?.id),
      }));

      dispatch(saveCourtsData(response?.data?.content));
      setFilteredCourts(courtsData);
      setTotalElements(totalElements);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching courts:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActionButtons = (courtId) => (
    <div>
      <button
        className="btn p-0 me-3"
        onClick={() => {
          setSelectedCourt(courtId);
          setIsEditOpen(true);
        }}
        title="Edit"
      >
        <FaRegEdit size={20} className="text-success" />
      </button>
      <button
        className="btn p-0 me-3"
        onClick={() => {
          setSelectedCourt(courtId);
          setIsRemoveOpen(true);
        }}
        title="Delete"
      >
        <AiOutlineDelete size={22} className="text-danger" />
      </button>
      <button
        className="btn p-0"
        onClick={() => {
          setSelectedCourt(courtId);
          setIsViewOpen(true);
        }}
        title="View Info"
      >
        <BsInfoCircle size={20} className="text-primary" />
      </button>
    </div>
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchCourts(filters);
    setIsDropdownFilterOpen(false);
  };

  const clearFilters = (e) => {
    e.preventDefault();
    const cleared = {
      name: "",
      sport: "",
      access: "",
      city: "",
      longitude: "",
      latitude: "",
      distance: "",
      address: "",
    };
    setFilters(cleared);
    fetchCourts(cleared);
    setIsDropdownFilterOpen(false);
  };

  const headings = [
    { name: "Name", fieldName: "name", isSortable: true, sortKey: "none" },
    {
      name: "Address",
      fieldName: "address",
      isSortable: false,
      sortKey: "none",
    },
    {
      name: "Amenities",
      fieldName: "amenities",
      isSortable: false,
      sortKey: "none",
    },
    {
      name: "Actions",
      fieldName: "actions",
      isSortable: false,
      sortKey: "none",
    },
  ];

  const filtersDropdown = () => (
    <form className="p-2" style={{ fontSize: "0.85rem" }}>
      <div className="row mb-2">
        <div className="col-md-6">
          <label className="mb-1">Name:</label>
          <Input
            type="text"
            placeholder="Enter Court Name"
            value={filters?.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
            style={{ fontSize: "0.85rem" }}
          />
        </div>
        <div className="col-md-6">
          <label className="mb-1">Sport:</label>
          <Input
            type="select"
            options={sportsList}
            value={filters?.sport}
            onChange={(e) => setFilters({ ...filters, sport: e.target.value })}
            style={{ fontSize: "0.85rem" }}
          />
        </div>
      </div>
      <div className="row mb-2">
        <div className="col-md-6">
          <label className="mb-1">Access:</label>
          <Input
            type="select"
            options={[
              { label: "Select Access", value: "" },
              { label: "Yes", value: true },
              { label: "No", value: false },
              { label: "Both", value: "" },
            ]}
            value={filters?.access}
            onChange={(e) => setFilters({ ...filters, access: e.target.value })}
            style={{ fontSize: "0.85rem" }}
          />
        </div>
        <div className="col-md-6">
          <label className="mb-1">City:</label>
          <Input
            placeholder="Enter City"
            value={filters?.city}
            onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            style={{ fontSize: "0.85rem" }}
          />
        </div>
      </div>
      <div className="row mb-2">
        <div className="col-md-6">
          <label className="mb-1">Latitude:</label>
          <Input
            type="text"
            placeholder="Enter Latitude"
            value={filters?.latitude}
            onChange={(e) => setFilters({ ...filters, latitude: e.target.value })}
            style={{ fontSize: "0.85rem" }}
          />
        </div>
        <div className="col-md-6">
          <label className="mb-1">Longitude:</label>
          <Input
            type="text"
            placeholder="Enter Longitude"
            value={filters?.longitude}
            onChange={(e) => setFilters({ ...filters, longitude: e.target.value })}
            style={{ fontSize: "0.85rem" }}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <label className="mb-1">Distance (km):</label>
          <input
            type="range"
            className="form-range"
            min="0"
            max="50"
            step="1"
            value={filters?.distance || 0}
            onChange={(e) => setFilters({ ...filters, distance: e.target.value })}
            style={{ fontSize: "0.85rem" }}
          />
          <div className="text-end small">{filters?.distance || 0} miles</div>
        </div>
      </div>
      <div className="row mb-2">
        <div className="col-12">
          <label className="mb-1">Address or ZIP code:</label>
          <Input
            type="text"
            placeholder="Enter Address or ZIP code"
            value={filters?.address}
            onChange={(e) => setFilters({ ...filters, address: e.target.value })}
            style={{ fontSize: "0.85rem" }}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="d-flex justify-content-end gap-2">
        <Button
          text="Apply Filters"
          callBack={(e) => {
            applyFilters(e);
            setIsDropdownFilterOpen(false);
          }}
          style={{ fontSize: "0.85rem" }}
        />
        <Button
          text="Clear All"
          callBack={(e) => {
            clearFilters(e);
            setIsDropdownFilterOpen(false);
          }}
          style={{ fontSize: "0.85rem" }}
        />
      </div>
    </form>
  );

  return (
    <div className="mt-2">
      {loading && <Loader />}
      <Table
        headings={headings}
        data={filteredCourts}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        enablePagination={totalPages > 1}
        addBtnText="+ Add Court"
        addCallback={() => setIsAddOpen(true)}
        addBulkText="+ Bulk Upload"
        addBulkCallback={() => setIsAddBulkOpen(true)}
        filtersDropdown={() => filtersDropdown()}
        isDropdownFilterOpen={isDropdownFilterOpen}
        setIsDropdownFilterOpen={setIsDropdownFilterOpen}
        card={true}
        // enableSearch={true}
      />
      <AddCourtModal
        isAddOpen={isAddOpen}
        closeAddModal={() => setIsAddOpen(false)}
        fetchCourts={() => fetchCourts(filters)}
      />
      <AddMultipleCourtsModal
        isAddOpen={isAddBulkOpen}
        closeAddModal={() => setIsAddBulkOpen(false)}
        fetchCourts={() => fetchCourts(filters)}
      />
      <EditCourtModal
        isEditOpen={isEditOpen}
        closeEditModal={() => setIsEditOpen(false)}
        selectedCourt={selectedCourt}
        fetchCourts={() => fetchCourts(filters)}
      />
      <RemoveCourtModal
        isRemoveOpen={isRemoveOpen}
        closeRemoveModal={() => setIsRemoveOpen(false)}
        selectedCourt={selectedCourt}
        fetchCourts={() => fetchCourts(filters)}
      />
      <CourtDetailsModal
        isViewDetailsOpen={isViewOpen}
        closeDetailsModal={() => setIsViewOpen(false)}
        selectedCourtId={selectedCourt}
      />
    </div>
  );
};

export default CourtsList;
